/**
 * Real presence and active hours, as pure functions (the Activity page).
 *
 * Every open tab of the platform sends a small heartbeat (`POST /api/presence`): its random tab id, the page's
 * path, whether it is visible, and when the person last interacted with it (input, scroll, focus, typing, media
 * playing). Visible tabs beat every minute, hidden ones every five, and a closing tab says so. From that:
 *
 *  - **State.** Active: an interaction in the last two minutes on a tab still open. Idle: a tab still open, no
 *    interaction for longer (a PC left on overnight stays idle all night). Away: no open tab - the last beat is too
 *    old for how often that tab beats, or the tab said it was closing.
 *  - **Active hours.** Each beat that carries a new interaction time records that instant. Instants no more than
 *    `STRETCH_GAP_MS` apart join into one stretch of work; a stretch also counts the `STRETCH_LEAD_MS` before its
 *    first instant (a beat samples the minute behind it). The sum of the stretches is the active time. The server
 *    groups the stretches in SQL with these same constants (`services/presence.ts`) and splits them into the
 *    viewer's days here.
 */

export type PresenceState = "active" | "idle" | "away";

export const PRESENCE = {
  /** An interaction this recent makes a person active. */
  ACTIVE_WINDOW_MS: 2 * 60_000,
  /** How often a visible tab beats, and a hidden one. */
  BEAT_VISIBLE_MS: 60_000,
  BEAT_HIDDEN_MS: 5 * 60_000,
  /** A tab whose last beat is older than this (three visible beats, two hidden ones and a minute) is gone. */
  VISIBLE_STALE_MS: 3 * 60_000,
  HIDDEN_STALE_MS: 11 * 60_000,
  /** The longest pause inside one stretch of work. */
  STRETCH_GAP_MS: 5 * 60_000,
  /** Credited before a stretch's first recorded interaction. */
  STRETCH_LEAD_MS: 60_000,
} as const;

type Instant = string | number | Date;
const ms = (v: Instant): number => (typeof v === "number" ? v : new Date(v).getTime());

/** One tab as `presence_tabs` keeps it. */
export interface PresenceTab {
  lastBeatAt: Instant;
  lastInputAt: Instant | null;
  visible: boolean;
  closed: boolean;
  page: string | null;
}

export interface PresenceSummary {
  state: PresenceState;
  /** The latest interaction on any tab, open or not. */
  lastActiveAt: number | null;
  lastBeatAt: number | null;
  /** The page of the open tab touched last. */
  page: string | null;
  /** Tabs still open. */
  openTabs: number;
}

/** Whether a tab still counts as open at `now`. */
export function tabOpen(tab: PresenceTab, now: number): boolean {
  if (tab.closed) return false;
  const age = now - ms(tab.lastBeatAt);
  return age <= (tab.visible ? PRESENCE.VISIBLE_STALE_MS : PRESENCE.HIDDEN_STALE_MS);
}

/** One person's state from all their tabs. `fallbackLastActive` stands in for someone with no tab yet. */
export function presenceOf(tabs: readonly PresenceTab[], now: number, fallbackLastActive: Instant | null = null): PresenceSummary {
  let lastActiveAt: number | null = fallbackLastActive === null ? null : ms(fallbackLastActive);
  let lastBeatAt: number | null = null;
  const open: PresenceTab[] = [];
  for (const tab of tabs) {
    const input = tab.lastInputAt === null ? null : ms(tab.lastInputAt);
    if (input !== null && Number.isFinite(input) && (lastActiveAt === null || input > lastActiveAt)) lastActiveAt = input;
    const beat = ms(tab.lastBeatAt);
    if (lastBeatAt === null || beat > lastBeatAt) lastBeatAt = beat;
    if (tabOpen(tab, now)) open.push(tab);
  }
  if (open.length === 0) return { state: "away", lastActiveAt, lastBeatAt, page: null, openTabs: 0 };
  const touched = [...open].sort((a, b) => (b.lastInputAt === null ? 0 : ms(b.lastInputAt)) - (a.lastInputAt === null ? 0 : ms(a.lastInputAt)) || ms(b.lastBeatAt) - ms(a.lastBeatAt))[0]!;
  const openInput = Math.max(...open.map((t) => (t.lastInputAt === null ? -Infinity : ms(t.lastInputAt))));
  const state: PresenceState = Number.isFinite(openInput) && now - openInput <= PRESENCE.ACTIVE_WINDOW_MS ? "active" : "idle";
  return { state, lastActiveAt, lastBeatAt, page: touched.page, openTabs: open.length };
}

/** A stretch of work, in epoch milliseconds, end inclusive of its last interaction. */
export interface Stretch {
  start: number;
  end: number;
}

/** Interaction instants into stretches of work (the rule in the header). */
export function activeStretches(instants: readonly Instant[], gapMs: number = PRESENCE.STRETCH_GAP_MS, leadMs: number = PRESENCE.STRETCH_LEAD_MS): Stretch[] {
  const sorted = [...new Set(instants.map(ms).filter(Number.isFinite))].sort((a, b) => a - b);
  const out: Stretch[] = [];
  for (const t of sorted) {
    const last = out.at(-1);
    if (last && t - last.end <= gapMs) last.end = t;
    else out.push({ start: t - leadMs, end: t });
  }
  return out;
}

/** The total length of stretches (overlaps counted once), in seconds. */
export function stretchSeconds(stretches: readonly Stretch[]): number {
  const sorted = [...stretches].sort((a, b) => a.start - b.start);
  let total = 0;
  let reach = -Infinity;
  for (const s of sorted) {
    const from = Math.max(s.start, reach);
    if (s.end > from) total += s.end - from;
    reach = Math.max(reach, s.end);
  }
  return Math.round(total / 1000);
}

/* ------------------------------------------------------------------------------------------- calendar days */

const dayFormats = new Map<string, Intl.DateTimeFormat>();
function dayFormat(tz: string): Intl.DateTimeFormat {
  let f = dayFormats.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
    dayFormats.set(tz, f);
  }
  return f;
}

/** The calendar day an instant falls on in `tz`, `YYYY-MM-DD`. */
export function localDay(at: number, tz: string): string {
  const parts = dayFormat(tz).formatToParts(new Date(at));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The first instant of a local day: the smallest `t` with `localDay(t) === day` (DST-safe, by bisection). */
export function startOfLocalDay(day: string, tz: string): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  // A local midnight lies within 14 hours either side of the UTC one.
  let lo = Date.UTC(y, m - 1, d) - 15 * 3_600_000;
  let hi = Date.UTC(y, m - 1, d) + 15 * 3_600_000;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (localDay(mid, tz) < day) lo = mid;
    else hi = mid;
  }
  return hi;
}

/** The day after `day`, `YYYY-MM-DD`. */
export function nextDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** Every day from `from` to `to`, inclusive. */
export function localDaysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 400; d = nextDay(d)) out.push(d);
  return out;
}

/**
 * Seconds of work per local day, the stretches clipped to `[from, to)` and split at each midnight in `tz`.
 * Overlapping stretches (two tabs) count once.
 */
export function secondsByDay(stretches: readonly Stretch[], tz: string, clip?: { from: number; to: number }): Map<string, number> {
  const pieces = new Map<string, Stretch[]>();
  for (const raw of stretches) {
    let start = clip ? Math.max(raw.start, clip.from) : raw.start;
    const end = clip ? Math.min(raw.end, clip.to) : raw.end;
    while (start < end) {
      const day = localDay(start, tz);
      const boundary = startOfLocalDay(nextDay(day), tz);
      const stop = Math.min(end, boundary);
      pieces.set(day, [...(pieces.get(day) ?? []), { start, end: stop }]);
      start = stop;
    }
  }
  const out = new Map<string, number>();
  for (const [day, list] of [...pieces].sort(([a], [b]) => a.localeCompare(b))) out.set(day, stretchSeconds(list));
  return out;
}

/** Whether `tz` names an IANA zone this runtime knows. */
export function isTimeZoneName(tz: string): boolean {
  if (!/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+){0,2}$/.test(tz) && tz !== "UTC") return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** "3 h 25 min", "45 min", "0 min" - for the active-hours figures. */
export function formatActiveDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
